import {type InfiniteData, useMutation, useQueryClient} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import {projectsQueryKey} from '../../queries/projects'
import type {ProjectsResponse} from './types'

interface UseUnlinkProjectOptions {
  owner: string
  repo: string
}

interface UnlinkProjectVariables {
  projectId: number
}

interface UnlinkProjectContext {
  /**
   * Snapshot of all matching `[..., owner, repo]` cache entries taken in `onMutate`,
   * keyed by their full queryKey so `onError` can restore each one verbatim.
   */
  previousEntries: Array<[readonly unknown[], InfiniteData<ProjectsResponse> | undefined]>
}

/**
 * Mirrors the Rails `Repos::MemexesController#unlink` form submission triggered by the
 * "Remove project" item in `Memex::ProjectList::ActionMenuComponent`. Optimistically
 * removes the project from every cached pages list under
 * `projectsQueryKey(owner, repo)` and decrements the matching
 * open/closed count. Restores the snapshot on error so the row reappears.
 *
 * No `onSettled` invalidation: paginated cursors invalidate poorly. The next user
 * navigation/refetch will reconcile counts naturally.
 */
export function useUnlinkProject({owner, repo}: UseUnlinkProjectOptions) {
  const queryClient = useQueryClient()

  return useMutation<void, Error, UnlinkProjectVariables, UnlinkProjectContext>({
    mutationFn: async ({projectId}) => {
      const response = await verifiedFetchJSON(`/${owner}/${repo}/projects/beta/unlink/${projectId}`, {
        method: 'DELETE',
      })
      if (!response.ok) {
        throw new Error(`Failed to unlink project: ${response.status}`)
      }
    },
    onMutate: async ({projectId}) => {
      // Pause any in-flight refetches so they can't overwrite our optimistic update
      // with a stale response after we return.
      await queryClient.cancelQueries({queryKey: projectsQueryKey(owner, repo)})

      const previousEntries = queryClient.getQueriesData<InfiniteData<ProjectsResponse>>({
        queryKey: projectsQueryKey(owner, repo),
      })

      for (const [key, data] of previousEntries) {
        if (!data) continue

        let removedClosed = false
        let removedOpen = false
        const nextPages = data.pages.map(page => {
          const nextProjects = page.projects.filter(project => {
            if (project.id !== projectId) return true
            if (project.closed) {
              removedClosed = true
            } else {
              removedOpen = true
            }
            return false
          })
          return {...page, projects: nextProjects}
        })

        if (!removedOpen && !removedClosed) {
          // Project wasn't in this cache entry; leave the snapshot in place but skip
          // the rewrite to avoid forcing a re-render.
          continue
        }

        // Decrement counts uniformly across all pages so the header stays consistent
        // regardless of which page the user is currently viewing.
        const nextPagesWithCounts = nextPages.map(page => ({
          ...page,
          openCount: removedOpen ? Math.max(page.openCount - 1, 0) : page.openCount,
          closedCount: removedClosed ? Math.max(page.closedCount - 1, 0) : page.closedCount,
        }))

        queryClient.setQueryData<InfiniteData<ProjectsResponse>>(key, {
          ...data,
          pages: nextPagesWithCounts,
        })
      }

      return {previousEntries}
    },
    onError: (_error, _variables, context) => {
      if (!context) return
      for (const [key, data] of context.previousEntries) {
        queryClient.setQueryData(key, data)
      }
    },
  })
}
